import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, ArrowLeft, Camera } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { api } from "../lib/api";

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/analyses/${id}`)
      .then(({ data }) => setDoc(data))
      .catch(() => toast.error("Could not load analysis"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="px-6 py-12 text-slate-500" data-testid="results-loading">
        Loading…
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="px-6 py-8" data-testid="results-notfound">
        <p className="text-slate-600 mb-4">This analysis is unavailable.</p>
        <Link to="/" className="mm-btn-secondary">
          Back home
        </Link>
      </div>
    );
  }

  const a = doc.analysis;
  const low = Number(a.sell_fast_price || 0);
  const mid = Number(a.recommended_price || 0);
  const high = Number(a.highest_reasonable_price || 0);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  // For the range bar we anchor low=0%, high=100% and place recommended between
  const midPct =
    high > low ? Math.min(100, Math.max(0, ((mid - low) / (high - low)) * 100)) : 50;

  return (
    <div className="px-6 pt-4 pb-8" data-testid="results-page">
      <button
        className="text-slate-500 text-sm inline-flex items-center gap-1 mb-3"
        onClick={() => navigate("/")}
        data-testid="back-btn"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Item header — compact */}
      <div className="flex items-center gap-3 mb-5" data-testid="item-header">
        <img
          src={doc.image_base64}
          alt="item"
          className="w-14 h-14 rounded-2xl object-cover border border-slate-200 shrink-0"
        />
        <div className="min-w-0">
          <div className="mm-label">Analyzed</div>
          <h1
            className="text-lg font-extrabold text-slate-900 leading-tight truncate"
            data-testid="item-name-heading"
          >
            {a.item_name}
          </h1>
        </div>
      </div>

      {/* Price range bar */}
      <div className="mm-card mb-4" data-testid="price-range-card">
        <div className="mm-label mb-4 text-center">Recommended listing price</div>

        <div
          className="font-mono font-extrabold text-5xl text-blue-700 text-center leading-none"
          data-testid="price-recommended"
        >
          ${mid.toLocaleString()}
        </div>
        <div className="text-center text-xs text-slate-500 mt-2 mb-6">
          Best balance of speed &amp; profit
        </div>

        {/* Bar */}
        <div className="relative">
          <div className="h-2 rounded-full bg-gradient-to-r from-emerald-300 via-blue-400 to-violet-300" />
          {/* Recommended marker */}
          <div
            className="absolute -top-1.5 w-5 h-5 rounded-full bg-blue-700 border-4 border-white shadow-md"
            style={{ left: `calc(${midPct}% - 10px)` }}
            aria-label="Recommended price marker"
          />
          {/* End caps */}
          <div className="absolute -top-1 left-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
          <div className="absolute -top-1 right-0 w-3 h-3 rounded-full bg-violet-500 border-2 border-white" />
        </div>

        <div className="flex justify-between items-start mt-3 gap-3">
          <div className="text-left" data-testid="price-sell-fast">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
              Sell fast
            </div>
            <div className="font-mono font-bold text-slate-900">
              ${low.toLocaleString()}
            </div>
          </div>
          <div className="text-right" data-testid="price-highest">
            <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700">
              Highest ask
            </div>
            <div className="font-mono font-bold text-slate-900">
              ${high.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Compact accordion for detail */}
      <Accordion type="single" collapsible className="mb-6" data-testid="details-accordion">
        <AccordionItem
          value="why"
          className="border border-slate-200 rounded-2xl bg-white px-4 mb-2 last:mb-2"
          data-testid="acc-why"
        >
          <AccordionTrigger className="hover:no-underline py-3 text-sm font-bold text-slate-800">
            Why this price?
          </AccordionTrigger>
          <AccordionContent className="text-sm text-slate-700 leading-relaxed pb-4">
            {a.explanation}
          </AccordionContent>
        </AccordionItem>

        {a.comparables?.length > 0 && (
          <AccordionItem
            value="comps"
            className="border border-slate-200 rounded-2xl bg-white px-4 mb-2"
            data-testid="acc-comps"
          >
            <AccordionTrigger className="hover:no-underline py-3 text-sm font-bold text-slate-800">
              Comparable items
              <span className="ml-2 text-xs font-mono text-slate-500">
                {a.comparables.length}
                {a.live_comps_count > 0 && (
                  <span className="text-emerald-700 ml-1">
                    · {a.live_comps_count} live
                  </span>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <ul className="space-y-3">
                {a.comparables.slice(0, 6).map((c, idx) => (
                  <li
                    key={idx}
                    className="flex items-start justify-between gap-3"
                    data-testid={`comp-${idx}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">
                        {c.marketplace}
                      </div>
                      <div className="font-bold text-slate-900 text-xs truncate">
                        {c.title}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-2">
                        <span>{c.condition}</span>
                        <span className="text-slate-300">•</span>
                        {c.status === "sold" ? (
                          <span className="text-slate-700">Sold</span>
                        ) : (
                          <span className="text-emerald-700 font-bold inline-flex items-center gap-1">
                            <span className="mm-live-dot" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-extrabold text-slate-900 text-sm">
                        ${Number(c.price).toLocaleString()}
                      </div>
                      {c.source_url && (
                        <a
                          href={c.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-blue-700 inline-flex items-center gap-1"
                        >
                          View <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        )}

        {a.listing && (
          <AccordionItem
            value="listing"
            className="border border-slate-200 rounded-2xl bg-white px-4"
            data-testid="acc-listing"
          >
            <AccordionTrigger className="hover:no-underline py-3 text-sm font-bold text-slate-800">
              Ready-to-post listing
            </AccordionTrigger>
            <AccordionContent className="pb-4 space-y-3">
              <div>
                <div className="mm-label mb-1">Title</div>
                <div
                  className="font-bold text-slate-900 text-sm"
                  data-testid="listing-title"
                >
                  {a.listing.title}
                </div>
                <button
                  className="mt-2 text-xs text-blue-700 font-bold inline-flex items-center gap-1"
                  onClick={() => copy(a.listing.title, "Title")}
                  data-testid="copy-title-btn"
                >
                  <Copy size={12} /> Copy title
                </button>
              </div>
              <div>
                <div className="mm-label mb-1">Description</div>
                <p
                  className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed"
                  data-testid="listing-description"
                >
                  {a.listing.description}
                </p>
                <button
                  className="mt-2 text-xs text-blue-700 font-bold inline-flex items-center gap-1"
                  onClick={() => copy(a.listing.description, "Description")}
                  data-testid="copy-description-btn"
                >
                  <Copy size={12} /> Copy description
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px] pt-2 border-t border-slate-100">
                <div>
                  <div className="mm-label">Category</div>
                  <div className="font-bold text-slate-900 mt-0.5">
                    {a.listing.category}
                  </div>
                </div>
                <div>
                  <div className="mm-label">Condition</div>
                  <div className="font-bold text-slate-900 mt-0.5">
                    {a.listing.condition}
                  </div>
                </div>
                <div>
                  <div className="mm-label">Start</div>
                  <div className="font-mono font-bold text-blue-700 mt-0.5">
                    ${a.listing.suggested_start_price}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      <button
        className="mm-btn-primary"
        onClick={() => navigate("/scan")}
        data-testid="analyze-another-btn"
      >
        <Camera size={20} />
        Analyze another item
      </button>
    </div>
  );
}
